import { FileText } from 'lucide-react';
import { RESOURCES_STRINGS } from '../constants/resources-strings';
import { ResourcePartnerCredit } from './ResourcePartnerCredit';
import { resourceCoverUrl } from '../resource-cover';
import type { FeaturedResourceCardProps } from '../types';
import type { ResourceType } from '@/lib/types/resources.types';

const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  tools_templates: 'Template',
  events_webinars: 'Webinar',
};

/**
 * Formats an ISO date string to "Month Year" (e.g. "September 2026").
 */
function formatAddedDate(isoDate: string): string {
  try {
    return new Date(isoDate).toLocaleDateString('en-GB', {
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

export interface FeaturedResourceCardComponentProps extends FeaturedResourceCardProps {
  /** Called when the user clicks "Open Resource". Caller decides the URL logic. */
  onOpen: () => void;
}

/**
 * Featured resource card — full-width white card shown at the top of the
 * Resources landing page when a featured item exists.
 *
 * Matches Figma node 192:3465: amber badge, bold title, meta row, separator,
 * footer with date + "Open Resource →" link.
 *
 * Laid out like the events featured card (FR-159): copy on the left, a 16:9 photo
 * about 44% of the card width on the right, stacked ABOVE the copy when the card
 * is narrow. It lays out by its own width (container query), not the viewport,
 * because the card can sit in columns of different widths. Falls back to a
 * placeholder photo until an admin uploads a cover image.
 */
export function FeaturedResourceCard({
  title,
  description,
  resourceType,
  createdAt,
  trustedPartner,
  coverImageUrl,
  onOpen,
}: FeaturedResourceCardComponentProps) {
  const addedDate = formatAddedDate(createdAt);
  const typeLabel = RESOURCE_TYPE_LABELS[resourceType] ?? 'Resource';
  const badgeText = `FEATURED ${typeLabel.toUpperCase()}`;
  const coverUrl = resourceCoverUrl(coverImageUrl);

  return (
    <article className="border-card-border shadow-card rounded-2xl border bg-white p-10">
      {/* Everything above the separator: copy on the left, photo on the right (on top when narrow).
          `@container` is the row's wrapper, since a container query only reaches its descendants. */}
      <div className="@container mb-6">
        <div className="flex flex-col gap-6 @3xl:flex-row @3xl:items-center @3xl:gap-8">
          <div className="min-w-0 flex-1">
            {/* Badge */}
            <div className="mb-4">
              <span className="inline-flex items-center rounded bg-[#FED13D15] px-2 py-0.5 text-[0.6875rem] font-semibold tracking-wide text-[#FED13D] uppercase">
                {badgeText}
              </span>
            </div>

            {/* Title */}
            <h2 className="mb-3 text-[22px] leading-none font-bold text-[#001928]">{title}</h2>

            {/* Description */}
            <p className="mb-4 line-clamp-2 text-[15px] leading-relaxed text-[#6B7280]">
              {description}
            </p>

            {/* Meta badges */}
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5 text-xs font-medium text-[#4DBA72]">
                <FileText size={14} aria-hidden className="shrink-0" />
                {typeLabel}
              </span>
            </div>
          </div>

          {/* Cover photo — 16:9, about 44% of the width; first when narrow. Same box as FeaturedEventCard. */}
          <div className="bg-bg order-first aspect-video w-full shrink-0 overflow-hidden rounded-lg @3xl:order-none @3xl:w-[44%] @3xl:max-w-[712px]">
            {coverUrl && <img src={coverUrl} alt="" className="h-full w-full object-cover" />}
          </div>
        </div>
      </div>

      {/* Partner credit (FR-278): the featured card counts as a card for
          A-299, so it carries the same attribution as the grid cards. */}
      {trustedPartner && (
        <div className="mb-5">
          <ResourcePartnerCredit partner={trustedPartner} />
        </div>
      )}

      {/* Separator */}
      <hr className="border-[#F7F9FB]" style={{ borderTopWidth: '2px' }} />

      {/* Footer */}
      <div className="mt-5 flex items-center justify-between">
        <span className="text-[11px] text-[#A0AEBC]">
          {RESOURCES_STRINGS.ADDED_PREFIX} {addedDate}
        </span>
        <button
          type="button"
          onClick={onOpen}
          className="text-[14px] font-semibold text-[#4DBA72] hover:underline focus-visible:ring-2 focus-visible:ring-[#4DBA72] focus-visible:outline-none"
        >
          {RESOURCES_STRINGS.OPEN_RESOURCE}
        </button>
      </div>
    </article>
  );
}
